import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../../helpers/apiRequester'
import styles from './styles.module.scss'

// ─── types ─────────────────────────────────────────────────────────────────

type Dataset = 'sent' | 'signed' | 'created'

interface SentRow {
    id: number
    agent: string | null
    client_id: number | null
    case_ref: string | null
    token: string | null
    send_type: 'email' | 'sms' | string | null
    recipient: string | null
    reused: number | boolean | null
    sent_datetime: string | null
    firstname: string | null
    lastname: string | null
    client_email: string | null
    client_telephone: string | null
}

interface SignedRow {
    id: number
    case_ref: string | null
    client_id: number | null
    agent: string | null
    status: string | null
    signature_date: string | null
    token: string | null
    firstname: string | null
    lastname: string | null
    client_email: string | null
    client_telephone: string | null
}

interface CreatedRow {
    id: number
    title: string | null
    firstname: string | null
    lastname: string | null
    email: string | null
    telephone: string | null
    dob: string | null
    postcode: string | null
    added_by: string | null
    created_at: string | null
}

type AnyRow = SentRow | SignedRow | CreatedRow

interface HistoryResponse {
    success: boolean
    dataset?: Dataset
    rows?: AnyRow[]
    total?: number
    page?: number
    limit?: number
    pageCount?: number
    message?: string
}

const TABS: { id: Dataset; label: string; reportType: string }[] = [
    { id: 'sent', label: 'Links Sent', reportType: 'pcp-referral-history-sent' },
    { id: 'signed', label: 'Signed', reportType: 'pcp-referral-history-signed' },
    { id: 'created', label: 'New Clients', reportType: 'pcp-referral-history-created' },
]

const PAGE_SIZE = 25

// ─── small render helpers ─────────────────────────────────────────────────

const fmtDateTime = (raw: string | null | undefined) => {
    if (!raw) return ''
    const d = new Date(raw)
    if (isNaN(d.getTime())) return raw
    return d.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const fmtDate = (raw: string | null | undefined) => {
    if (!raw) return ''
    const d = new Date(raw)
    if (isNaN(d.getTime())) return raw
    return d.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    })
}

const fullName = (firstname?: string | null, lastname?: string | null) => {
    const parts = [firstname, lastname].filter((v) => v && String(v).trim().length > 0)
    return parts.join(' ').trim()
}

// ─── main component ───────────────────────────────────────────────────────

const PcpReferralEsignHistoryView = () => {
    const [activeTab, setActiveTab] = useState<Dataset>('sent')

    // filter state — per-tab so switching tabs preserves filters
    const [search, setSearch] = useState('')
    const [dateFrom, setDateFrom] = useState('')
    const [dateTo, setDateTo] = useState('')
    const [page, setPage] = useState(1)

    const [rows, setRows] = useState<AnyRow[]>([])
    const [total, setTotal] = useState(0)
    const [pageCount, setPageCount] = useState(1)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [downloadingType, setDownloadingType] = useState<'all' | 'group' | null>(null)

    // Debounced search — avoid hammering the API on every keystroke.
    const [debouncedSearch, setDebouncedSearch] = useState('')
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 350)
        return () => clearTimeout(t)
    }, [search])

    // Avoid re-fetching on stale params from a previous tab.
    const requestIdRef = useRef(0)

    const fetchRows = useCallback(async () => {
        const myId = ++requestIdRef.current
        setLoading(true)
        setError(null)
        try {
            const res = await api({
                url: '/pcp-referral-esign-history',
                data: {
                    dataset: activeTab,
                    page,
                    limit: PAGE_SIZE,
                    search: debouncedSearch,
                    dateFrom,
                    dateTo,
                },
            })
            if (requestIdRef.current !== myId) return // a newer request superseded us
            const data: HistoryResponse = res.data
            if (data?.success) {
                setRows(data.rows || [])
                setTotal(data.total || 0)
                setPageCount(Math.max(1, data.pageCount || 1))
            } else {
                setRows([])
                setTotal(0)
                setPageCount(1)
                setError(data?.message || 'Failed to load history')
            }
        } catch (err: any) {
            if (requestIdRef.current !== myId) return
            const msg = err?.response?.data?.message || err?.message || 'Network error'
            setRows([])
            setTotal(0)
            setPageCount(1)
            setError(`Failed to load history: ${msg}`)
        } finally {
            if (requestIdRef.current === myId) setLoading(false)
        }
    }, [activeTab, page, debouncedSearch, dateFrom, dateTo])

    useEffect(() => {
        fetchRows()
    }, [fetchRows])

    // Reset to page 1 whenever filters (other than page itself) change.
    useEffect(() => {
        setPage(1)
    }, [activeTab, debouncedSearch, dateFrom, dateTo])

    const activeTabConfig = useMemo(() => TABS.find((t) => t.id === activeTab)!, [activeTab])

    const clearFilters = () => {
        setSearch('')
        setDateFrom('')
        setDateTo('')
    }

    // CSV download — applies current dataset + filters. `scope='all'` clears
    // search so users get every row in that group within the date range.
    const handleDownload = async (scope: 'group' | 'all') => {
        setDownloadingType(scope)
        Loading.standard('Generating report…')
        try {
            const usingDates = Boolean(dateFrom && dateTo)
            const payload: Record<string, any> = {
                reportType: activeTabConfig.reportType,
                isFullReport: !usingDates,
                search: scope === 'group' ? debouncedSearch : '',
            }
            if (usingDates) {
                payload.fromDate = new Date(`${dateFrom}T00:00:00Z`).toISOString()
                payload.toDate = new Date(`${dateTo}T23:59:59Z`).toISOString()
            }
            const res = await api({ url: '/download-report', data: payload })
            if (res.data?.fileUrl) {
                window.open(res.data.fileUrl, '_blank')
                Report.success('Report ready', `Downloaded ${res.data.rowCount ?? ''} rows.`, 'OK')
            } else {
                Report.failure('Error', res.data?.message || 'No file URL returned', 'OK')
            }
        } catch (err: any) {
            const msg = err?.response?.data?.message || err?.message || 'Network error'
            Report.failure('Error', `Failed to generate report: ${msg}`, 'OK')
        } finally {
            setDownloadingType(null)
            Loading.remove()
        }
    }

    // ─── per-dataset rendering ────────────────────────────────────────────

    const renderRows = () => {
        if (loading) {
            return (
                <tr>
                    <td className={styles.loadingRow} colSpan={99}>
                        Loading…
                    </td>
                </tr>
            )
        }
        if (!rows.length) {
            return (
                <tr>
                    <td className={styles.empty} colSpan={99}>
                        No records match the current filters.
                    </td>
                </tr>
            )
        }

        if (activeTab === 'sent') {
            return (rows as SentRow[]).map((r) => (
                <tr key={`sent-${r.id}`}>
                    <td>{fmtDateTime(r.sent_datetime)}</td>
                    <td>{r.agent || <span className={styles.muted}>—</span>}</td>
                    <td>{fullName(r.firstname, r.lastname) || <span className={styles.muted}>—</span>}</td>
                    <td>{r.recipient || <span className={styles.muted}>—</span>}</td>
                    <td>
                        {r.send_type === 'email' && <span className={`${styles.pill} ${styles.pillEmail}`}>Email</span>}
                        {r.send_type === 'sms' && <span className={`${styles.pill} ${styles.pillSms}`}>SMS</span>}
                        {r.send_type && r.send_type !== 'email' && r.send_type !== 'sms' && <span>{r.send_type}</span>}
                    </td>
                    <td>{r.case_ref || <span className={styles.muted}>—</span>}</td>
                    <td>
                        {r.reused ? <span className={`${styles.pill} ${styles.pillReused}`}>Reused</span> : 'New'}
                    </td>
                    <td>{r.client_id || <span className={styles.muted}>—</span>}</td>
                </tr>
            ))
        }

        if (activeTab === 'signed') {
            return (rows as SignedRow[]).map((r) => (
                <tr key={`signed-${r.id}`}>
                    <td>{fmtDateTime(r.signature_date)}</td>
                    <td>{r.agent || <span className={styles.muted}>—</span>}</td>
                    <td>{fullName(r.firstname, r.lastname) || <span className={styles.muted}>—</span>}</td>
                    <td>{r.client_email || <span className={styles.muted}>—</span>}</td>
                    <td>{r.client_telephone || <span className={styles.muted}>—</span>}</td>
                    <td>{r.case_ref || <span className={styles.muted}>—</span>}</td>
                    <td>
                        <span className={`${styles.pill} ${styles.pillSigned}`}>{r.status || 'Signed'}</span>
                    </td>
                    <td>{r.client_id || <span className={styles.muted}>—</span>}</td>
                </tr>
            ))
        }

        // created
        return (rows as CreatedRow[]).map((r) => (
            <tr key={`created-${r.id}`}>
                <td>{fmtDateTime(r.created_at)}</td>
                <td>{r.added_by || <span className={styles.muted}>—</span>}</td>
                <td>{fullName(r.firstname, r.lastname) || <span className={styles.muted}>—</span>}</td>
                <td>{r.email || <span className={styles.muted}>—</span>}</td>
                <td>{r.telephone || <span className={styles.muted}>—</span>}</td>
                <td>{fmtDate(r.dob) || <span className={styles.muted}>—</span>}</td>
                <td>{r.postcode || <span className={styles.muted}>—</span>}</td>
                <td>{r.id}</td>
            </tr>
        ))
    }

    const renderTableHead = () => {
        if (activeTab === 'sent') {
            return (
                <tr>
                    <th>Sent</th>
                    <th>Agent</th>
                    <th>Client</th>
                    <th>Recipient</th>
                    <th>Type</th>
                    <th>Case Ref</th>
                    <th>Status</th>
                    <th>Client ID</th>
                </tr>
            )
        }
        if (activeTab === 'signed') {
            return (
                <tr>
                    <th>Signed</th>
                    <th>Agent</th>
                    <th>Client</th>
                    <th>Email</th>
                    <th>Telephone</th>
                    <th>Case Ref</th>
                    <th>Status</th>
                    <th>Client ID</th>
                </tr>
            )
        }
        return (
            <tr>
                <th>Created</th>
                <th>Added By</th>
                <th>Client</th>
                <th>Email</th>
                <th>Telephone</th>
                <th>DOB</th>
                <th>Postcode</th>
                <th>Client ID</th>
            </tr>
        )
    }

    // Search is only meaningful on the two tabs the user explicitly asked about
    // (sent + signed). On "New Clients" we still allow it because the cost is low.
    const searchPlaceholder = useMemo(() => {
        switch (activeTab) {
            case 'sent':
                return 'Search by client, email, phone, agent, recipient…'
            case 'signed':
                return 'Search by client, email, phone, agent, case ref…'
            default:
                return 'Search by client, email, phone, added by…'
        }
    }, [activeTab])

    return (
        <div className={styles.container}>

            <div className={styles.tabs}>
                {TABS.map((tab) => {
                    const isActive = tab.id === activeTab
                    return (
                        <button
                            key={tab.id}
                            type="button"
                            className={`${styles.tabButton} ${isActive ? styles.tabButtonActive : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.label}
                            {isActive && total > 0 && <span className={styles.tabCount}>{total.toLocaleString()}</span>}
                        </button>
                    )
                })}
            </div>

            <div className={styles.toolbar}>
                <div className={styles.filterGroup}>
                    <div className={styles.filterField}>
                        <label htmlFor="esign-history-search">Search</label>
                        <input
                            id="esign-history-search"
                            type="search"
                            className={styles.searchInput}
                            placeholder={searchPlaceholder}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            autoComplete="off"
                        />
                    </div>
                    <div className={styles.filterField}>
                        <label htmlFor="esign-history-from">From</label>
                        <input
                            id="esign-history-from"
                            type="date"
                            className={styles.dateInput}
                            value={dateFrom}
                            max={dateTo || undefined}
                            onChange={(e) => setDateFrom(e.target.value)}
                        />
                    </div>
                    <div className={styles.filterField}>
                        <label htmlFor="esign-history-to">To</label>
                        <input
                            id="esign-history-to"
                            type="date"
                            className={styles.dateInput}
                            value={dateTo}
                            min={dateFrom || undefined}
                            onChange={(e) => setDateTo(e.target.value)}
                        />
                    </div>
                    <button
                        type="button"
                        className={styles.ghostButton}
                        onClick={clearFilters}
                        disabled={!search && !dateFrom && !dateTo}
                    >
                        Clear filters
                    </button>
                </div>
                <div className={styles.toolbarActions}>
                    <button
                        type="button"
                        className={styles.secondaryButton}
                        onClick={() => handleDownload('all')}
                        disabled={downloadingType !== null}
                        title="Download every record in this group (respects date range)"
                    >
                        {downloadingType === 'all' ? 'Generating…' : 'Download all'}
                    </button>
                    <button
                        type="button"
                        className={styles.primaryButton}
                        onClick={() => handleDownload('group')}
                        disabled={downloadingType !== null}
                        title="Download CSV of the currently filtered + searched view"
                    >
                        {downloadingType === 'group' ? 'Generating…' : 'Download filtered'}
                    </button>
                </div>
            </div>

            {error && <div className={styles.errorBanner}>{error}</div>}

            <div className={styles.tableWrapper}>
                <div className={styles.tableScroll}>
                    <table className={styles.dataTable}>
                        <thead>{renderTableHead()}</thead>
                        <tbody>{renderRows()}</tbody>
                    </table>
                </div>
            </div>

            <div className={styles.pagination}>
                <span className={styles.pageInfo}>
                    {total === 0
                        ? 'No results'
                        : `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(
                              page * PAGE_SIZE,
                              total,
                          )} of ${total.toLocaleString()}`}
                </span>
                <div className={styles.paginationControls}>
                    <button
                        type="button"
                        className={styles.ghostButton}
                        onClick={() => setPage(1)}
                        disabled={page <= 1 || loading}
                    >
                        « First
                    </button>
                    <button
                        type="button"
                        className={styles.ghostButton}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page <= 1 || loading}
                    >
                        ‹ Prev
                    </button>
                    <span>
                        Page {page} / {pageCount}
                    </span>
                    <button
                        type="button"
                        className={styles.ghostButton}
                        onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                        disabled={page >= pageCount || loading}
                    >
                        Next ›
                    </button>
                    <button
                        type="button"
                        className={styles.ghostButton}
                        onClick={() => setPage(pageCount)}
                        disabled={page >= pageCount || loading}
                    >
                        Last »
                    </button>
                </div>
            </div>
        </div>
    )
}

export default PcpReferralEsignHistoryView
